import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../auth/useAuth';
import {
  TimeSheet,
  WorkRolesDict,
  WorkTypesDict,
  ChargeCodesDict,
  CwUsersByMemberIdDict,
  DepartmentsDict,
  ReportsToDict,
  UserTypesDict,
  WorkRoleValidationsList,
} from '../types/api.types';

interface DataContextType {
  workRoles: WorkRolesDict;
  workTypes: WorkTypesDict;
  chargeCodes: ChargeCodesDict;
  cwUsersByMemberId: CwUsersByMemberIdDict;
  departmentsDict: DepartmentsDict;
  reportsToDict: ReportsToDict;
  userTypesDict: UserTypesDict;
  workRoleValidations: WorkRoleValidationsList;
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  fetchTimeSheets: (conditions?: string) => Promise<TimeSheet[]>;
  uploadWorkRoleValidations: (file: File) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessToken } = useAuth();
  const [workRoles, setWorkRoles] = useState<WorkRolesDict>({});
  const [workTypes, setWorkTypes] = useState<WorkTypesDict>({});
  const [chargeCodes, setChargeCodes] = useState<ChargeCodesDict>({});
  const [cwUsersByMemberId, setCwUsersByMemberId] = useState<CwUsersByMemberIdDict>({});
  const [departmentsDict, setDepartmentsDict] = useState<DepartmentsDict>({});
  const [reportsToDict, setReportsToDict] = useState<ReportsToDict>({});
  const [userTypesDict, setUserTypesDict] = useState<UserTypesDict>({});
  const [workRoleValidations, setWorkRoleValidations] = useState<WorkRoleValidationsList>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Set the token provider for the API service
  useEffect(() => {
    apiService.setTokenProvider(getAccessToken);
  }, [getAccessToken]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [roles, types, codes, usersByMemberId, roleValidations] = await Promise.all([
        apiService.getWorkRoles(),
        apiService.getWorkTypes(),
        apiService.getChargeCodes(),
        apiService.getActiveCwUsersByMemberId(),
        apiService.getWorkRoleValidations(),
      ]);

      setWorkRoles(roles);
      setWorkTypes(types);
      setChargeCodes(codes);
      setCwUsersByMemberId(usersByMemberId);
      setWorkRoleValidations(roleValidations);
      console.log('[DataContext] Work role validations loaded:', roleValidations.length, 'valid combinations', roleValidations);

      // DEBUG: log sample user ids and a few user objects
      const userIds = Object.keys(usersByMemberId).map(Number);
      console.log('[DataContext] Total CW users loaded:', userIds.length);
      console.log('[DataContext] Sample user ids (first 10):', userIds.slice(0, 10));
      console.log('[DataContext] Sample users (first 3):', Object.values(usersByMemberId).slice(0, 3).map(u => ({
        id: u.id, identifier: u.identifier, name: u.name,
        reportsTo: u.reportsTo, defaultDepartment: u.defaultDepartment
      })));

      // Build department and reportsTo lookup dictionaries from loaded users
      const depts: DepartmentsDict = {};
      const managers: ReportsToDict = {};
      const uTypes: UserTypesDict = {};
      Object.values(usersByMemberId).forEach((user) => {
        if (user.defaultDepartment?.id && user.defaultDepartment.name) {
          depts[user.defaultDepartment.id] = user.defaultDepartment.name;
        }
        const managerId = user.reportsTo?.id ?? user.timeApprover?.id;
        if (managerId) {
          // managerId is itself a user id — resolve the name from the
          // users dictionary so it stays consistent; fall back to the
          // embedded name if the manager isn't in the active set.
          const mgrUser = usersByMemberId[managerId];
          const mgrName = mgrUser?.name || user.reportsTo?.name || user.timeApprover?.name;
          if (mgrName) {
            managers[managerId] = mgrName;
          }
        }
        if (user.type?.id && user.type.name) {
          uTypes[user.type.id] = user.type.name;
        }
      });
      setDepartmentsDict(depts);
      setReportsToDict(managers);
      setUserTypesDict(uTypes);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTimeSheets = useCallback(async (conditions?: string): Promise<TimeSheet[]> => {
    return apiService.getTimeSheets(conditions);
  }, []);

  const uploadWorkRoleValidations = useCallback(async (file: File) => {
    const validations = await apiService.uploadWorkRoleValidations(file);
    setWorkRoleValidations(validations);
    console.log('[DataContext] Work role validations uploaded:', validations.length, 'valid combinations', validations);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const value: DataContextType = {
    workRoles,
    workTypes,
    chargeCodes,
    cwUsersByMemberId,
    departmentsDict,
    reportsToDict,
    userTypesDict,
    workRoleValidations,
    isLoading,
    error,
    refreshData: loadData,
    fetchTimeSheets,
    uploadWorkRoleValidations,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
